import {
  Box,
  Table,
  TableBody,
  TableCell as MUITableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import numeral from 'numeral';
import PropTypes from 'prop-types';
import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import ItemType from '@explorer/components/ui/item-type';
import PlanetOnlyChip from '@explorer/components/ui/planet-only-chip';
import {formatInstrumentLabel} from '@explorer/util/filters';

const TableCell = props => (
  <MUITableCell sx={{border: 1, borderColor: 'grey.500'}} {...props} />
);

const FIELDS = [
  {
    key: 'item_type',
  },
  {
    key: 'collection_mode',
  },
  {
    key: 'cloud_percent',
    formatter: value => `${value}%`,
  },
  {
    key: 'grid_cell',
  },
  {
    key: 'gsd',
    formatter: value => `${numeral(value).value()}m`,
  },
  {
    key: 'pixel_resolution',
    formatter: value => `${numeral(value).value()}m`,
  },
  {
    key: 'satellite_id',
  },
  {
    key: 'view_angle',
    formatter: value => `${numeral(value).format('0.0')}°`,
  },
  {
    key: 'sun_elevation',
    formatter: value => `${numeral(value).format('0.0')}°`,
  },
  {
    key: 'sun_azimuth',
    formatter: value => `${numeral(value).format('0.0')}°`,
  },
  {
    key: 'ground_control',
    formatter: value => (value === true ? 'true' : 'false'),
  },
  {
    key: 'ground_control_ratio',
    formatter: value => `${numeral(value).format('0.00')}`,
  },
  {
    key: 'quality_category',
  },
  {
    key: 'instrument',
    formatter: value => formatInstrumentLabel(value),
  },
  {
    key: 'availability',
    formatter: value => (
      <Trans i18nKey="common:date_time.med" values={{date: value}} />
    ),
    // this is just to add a "badge" of Planet Only and does nothing else
    // the API controls whether this data is returned
    planetOnly: true,
  },
  {
    key: 'publishing_stage',
  },
];

const MetadataTable = props => {
  const {t} = useTranslation();
  const properties = props.item.properties;

  const getRow = field => {
    let value = properties[field.key];
    if (field.key === 'item_type') {
      value = <ItemType itemType={value} showIcon={false} />;
    }

    if (field.key === 'cloud_percent') {
      value =
        properties['cloud_percent'] ??
        (properties['cloud_cover'] * 100).toFixed(2);
    }

    if (value === null || value === undefined) {
      return false;
    }

    return (
      <TableRow key={field.key}>
        <TableCell component="th" scope="row">
          <Tooltip title={t(`metadataModal.${field.key}.description`)}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography color="textSecondary">
                {t(`metadataModal.${field.key}.label`)}
              </Typography>
              {field.planetOnly && <PlanetOnlyChip />}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          {typeof value === 'object' && value}
          {typeof value !== 'object' && (
            <Typography data-qe={`${field.key}-table-value`}>
              {field.formatter ? field.formatter(value) : value}
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer sx={{minWidth: 300}}>
      <Table size="small">
        <TableBody>{FIELDS.map(getRow)}</TableBody>
      </Table>
    </TableContainer>
  );
};

MetadataTable.propTypes = {
  item: PropTypes.object,
};

export default MetadataTable;
